import {computed, type ComputedRef} from 'vue'
import type {DataColumn, DataError, DataProvider, DataRow} from '@hantera/design-system'
import type {JobsDashboardStore} from '@/stores/jobs/jobs-dashboard-store'
import type {JobDefinition} from '@/backend/jobs.types'

type ScheduleStatus = 'scheduled' | 'suspended' | 'none'

function scheduleStatus(def: JobDefinition): ScheduleStatus {
  const hasSchedules = (def.schedules?.length ?? 0) > 0
  if (!hasSchedules) return 'none'
  return def.scheduleSuspended ? 'suspended' : 'scheduled'
}

export class JobsDashboardDataProvider implements DataProvider {
  public readonly rows: ComputedRef<DataRow[]>
  public readonly isLoading: ComputedRef<boolean>
  public readonly allLoaded = computed(() => true)
  public readonly errors: ComputedRef<DataError[] | undefined>
  public readonly columns: ComputedRef<DataColumn[]>
  public readonly totalCount: ComputedRef<{ count: number } | undefined>

  constructor(
    private store: JobsDashboardStore,
    private t: (key: string, params?: Record<string, any>) => string,
  ) {
    this.isLoading = computed(() => this.store.isLoading)

    this.errors = computed(() => {
      if (this.store.error) {
        return [{ code: 'LOAD_ERROR', message: this.store.error }]
      }
      return undefined
    })

    this.rows = computed(() => {
      const scheduleByDefinition = new Map(
        this.store.jobDefinitions.map((d) => [d.jobDefinitionId, scheduleStatus(d)]),
      )

      return this.store.aggregatedStats.map((entry) => {
        const hasFailures = entry.totalFailed.greaterThan(0)
        const hasHighFailureRate =
          entry.successRate.lessThan(90) && entry.totalCompleted.greaterThan(0)

        let hue: string | undefined
        if (hasHighFailureRate) {
          hue = '--color-hue-red'
        } else if (hasFailures) {
          hue = '--color-hue-yellow'
        }

        return {
          key: entry.jobDefinitionId,
          hue,
          values: [
            entry.jobDefinitionId,
            scheduleByDefinition.get(entry.jobDefinitionId) ?? 'none',
            entry.totalPending,
            entry.totalCompleted,
            entry.totalSuccessful,
            entry.totalFailed,
            entry.successRate,
            entry.avgExecution,
            entry.minExecution,
            entry.maxExecution,
          ],
        } as DataRow
      })
    })

    this.columns = computed<DataColumn[]>(() => [
      {
        key: 'jobDefinitionId',
        label: this.t('columns.jobDefinitionId'),
        valueIdx: 0,
        canSort: false,
        canFilter: false,
        values: {
          type: 'text',
          isArray: false,
        },
      },
      {
        key: 'schedule',
        label: this.t('columns.schedule'),
        valueIdx: 1,
        canSort: false,
        canFilter: false,
        values: {
          type: 'enum',
          isArray: false,
          enums: [{
            values: [
              { key: 'scheduled', label: this.t('columns.scheduleStatus.scheduled'), hue: 130 },
              { key: 'suspended', label: this.t('columns.scheduleStatus.suspended'), hue: 45 },
              { key: 'none', label: this.t('columns.scheduleStatus.none') },
            ],
          }],
        },
      },
      {
        key: 'totalPending',
        label: this.t('columns.totalPending'),
        valueIdx: 2,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
        },
      },
      {
        key: 'totalCompleted',
        label: this.t('columns.totalCompleted'),
        valueIdx: 3,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
        },
      },
      {
        key: 'totalSuccessful',
        label: this.t('columns.successful'),
        valueIdx: 4,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
        },
      },
      {
        key: 'totalFailed',
        label: this.t('columns.failed'),
        valueIdx: 5,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
        },
      },
      {
        key: 'successRate',
        label: this.t('columns.successRate'),
        valueIdx: 6,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
          unitLabel: '%',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        },
      },
      {
        key: 'avgExecution',
        label: this.t('columns.avgExecution'),
        valueIdx: 7,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
          unitLabel: ' ms',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        },
      },
      {
        key: 'minExecution',
        label: this.t('columns.minExecution'),
        valueIdx: 8,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
          unitLabel: ' ms',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        },
      },
      {
        key: 'maxExecution',
        label: this.t('columns.maxExecution'),
        valueIdx: 9,
        canSort: false,
        canFilter: false,
        values: {
          type: 'number',
          unitLabel: ' ms',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        },
      },
    ])

    this.totalCount = computed(() => {
      const count = this.rows.value.length
      if (count === 0) return undefined
      return { count }
    })
  }
  
  public getRowHue(row: DataRow) {
    return (row as any).hue
  }

  public async refresh(): Promise<void> {
    await this.store.refresh()
  }

  public async loadMore(): Promise<void> {
    // All data is loaded at once, no pagination needed
  }
}
